import { useEffect } from 'react'
import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { CATEGORIES_PAGE_SIZE } from '../../consts/defaultConfigValues/defaultConstants'
import { useLazyGetCategories } from '../../graphQL/queries/getCategories'
import { ICategories } from '../../graphQL/queries/getCategories/types'
import {
  setCategories,
  setCategoriesPagination,
  setCategoryList,
  setFetchingMoreLoading,
  setPagination,
} from '../../redux/slices/catalog'
import {
  castICategoriesItemToICatalogItemArray,
  castICategoriesItemToICategoryArray,
  setCatalogPagination,
} from '../../utils/categoryUtils'
import { generateDataDogContext } from '../../utils/dataDog'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'

export const useFetchCategories = () => {
  const dispatch = useAppDispatch()
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)
  const categoriesPagination = useAppSelector(
    (state) => state.catalogSlice.categoriesPagination,
  )
  const categories = useAppSelector((state) => state.catalogSlice.categories)
  const categoryList = useAppSelector(
    (state) => state.catalogSlice.categoryList,
  )
  const pagination = useAppSelector((state) => state.catalogSlice.pagination)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  useEffect(() => {
    return () => {
      dispatch(setFetchingMoreLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCompleted = (data: ICategories) => {
    const { categories: categoriesResponse } = data
    dispatch(
      setCategoriesPagination({
        hasMore:
          categoriesResponse?.length === CATEGORIES_PAGE_SIZE ||
          categoriesResponse?.length === 0,
        pageNumber: (categoriesPagination?.pageNumber || 0) + 1,
        pageSize: CATEGORIES_PAGE_SIZE,
      }),
    )

    dispatch(
      setCategoryList([
        ...categoryList,
        ...castICategoriesItemToICategoryArray(categoriesResponse || []),
      ]),
    )
    const newCategories = castICategoriesItemToICatalogItemArray(
      categoriesResponse || [],
    )
    dispatch(setCategories([...categories, ...newCategories]))

    dispatch(
      setPagination({
        ...pagination,
        ...setCatalogPagination(newCategories),
      }),
    )
  }

  const onError = (error: ApolloError) => {
    const context = generateDataDogContext({
      title: 'could not fetch categories response',
      extraInfo: { function: 'fetchCategoriesResponse' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  const [getCategories] = useLazyGetCategories({ onCompleted, onError })

  const fetchCategories = async () => {
    try {
      if (categoriesPagination) {
        const { pageNumber, pageSize, hasMore } = categoriesPagination

        if (hasMore) {
          dispatch(setFetchingMoreLoading(true))

          await getCategories({
            variables: {
              storefrontName: storeName,
              pagination: {
                pageNumber: pageNumber + 1,
                pageSize,
              },
              sessionID: sessionId,
            },
          })
        }
      }
    } catch (exception) {
      const context = generateDataDogContext({
        title: 'could not fetch categories',
        extraInfo: { function: 'useFetchCategories' },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(exception, context)
    } finally {
      dispatch(setFetchingMoreLoading(false))
    }
  }
  return fetchCategories
}
